/**
 * Утилиты для работы с MIDI в pattern workflow.
 */

import type {
  Midi,
  MidiChannel,
  MidiNote,
  MidiTimeSignature,
} from "@midi-fantom-utils/midi-core";
import { measureBoundaries } from "./time.js";

/**
 * Считает ноты в указанном канале, попадающие в диапазон `[startTick, endTick)`.
 *
 * Используется PatternDialog для определения «есть ли ноты в этом срезе
 * для выбранного канала» — раньше считали по всему файлу, и кнопка
 * «Создать» оставалась активной для пустого среза, а `buildPattern`
 * тихо возвращал `{ ok: false, reason: "empty" }`.
 */
export const countNotesInRange = (
  midi: Midi,
  channel: MidiChannel,
  startTick: number,
  endTick: number
): number => {
  if (endTick <= startTick) return 0;
  let n = 0;
  for (const t of midi.tracks) {
    for (const note of t.notes) {
      const ch: MidiChannel = note.channel ?? t.channel;
      if (ch !== channel) continue;
      if (note.ticks >= endTick) continue;
      if (note.ticks + note.durationTicks <= startTick) continue;
      n += 1;
    }
  }
  return n;
};

/**
 * Считает ноты в указанном канале внутри диапазона тактов
 * `[startMeasure, endMeasure)` (1-based, endMeasure exclusive).
 * Использует `measureBoundaries` для TS-aware границ.
 */
export const countNotesInMeasureRange = (
  midi: Midi,
  channel: MidiChannel,
  startMeasure: number,
  endMeasure: number
): number => {
  const totalTicks = (() => {
    let max = 0;
    for (const t of midi.tracks) {
      for (const n of t.notes) {
        const end = n.ticks + n.durationTicks;
        if (end > max) max = end;
      }
    }
    return Math.max(max, midi.header.ppq * 4);
  })();
  const boundaries = measureBoundaries(
    midi.header.ppq,
    midi.header.timeSignatures,
    totalTicks
  );
  const startTick = boundaries[Math.max(0, startMeasure - 1)] ?? 0;
  const endTick =
    boundaries[Math.max(0, Math.min(boundaries.length, endMeasure) - 1)] ??
    totalTicks;
  return countNotesInRange(midi, channel, startTick, endTick);
};

// Suppress unused-import warning: keep explicit type so future readers
// see which `MidiNote`/`MidiTimeSignature` shape this helper relies on.
export type { MidiNote, MidiTimeSignature };
